import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Edit3,
  FileText,
  Loader2,
  PencilLine,
  RefreshCw,
  Sparkles,
} from 'lucide-react'

import { toast } from '@/hooks/use-toast'
import { api, ApiError, projectFileUrl } from '@/lib/api'
import { waitForPhaseCompletion } from '@/lib/phaseEvents'
import { parseBriefingSections } from '@/components/briefings/PhaseBriefingPanel'
import { carbonMarkdownToHtml } from '@/lib/carbonMarkdown'

type DecisionKind = null | 'modify'

export interface CarbonPhaseBriefingPanelProps {
  projectId: string
  runId?: number
  onApproved?: () => void
  onPhaseComplete?: () => void
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-cb-mono text-[10px] font-semibold tracking-[0.14em] text-cb-text-3">
      {children}
    </div>
  )
}

function BriefCard({ label, markdown }: { label: string; markdown: string }) {
  return (
    <section className="mt-[14px] rounded-[14px] border border-cb-border bg-cb-panel px-5 py-[18px] shadow-[0_1px_2px_rgba(20,16,12,.06)] first:mt-6">
      <Eyebrow>{label}</Eyebrow>
      <div className="mt-3">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={carbonMarkdownToHtml}>
          {markdown}
        </ReactMarkdown>
      </div>
    </section>
  )
}

export function CarbonPhaseBriefingPanel({
  projectId,
  runId = 1,
  onApproved,
  onPhaseComplete,
}: CarbonPhaseBriefingPanelProps) {
  const [decisionKind, setDecisionKind] = useState<DecisionKind>(null)
  const [modification, setModification] = useState('')
  const [briefingBody, setBriefingBody] = useState<string>('')

  const briefing = useQuery({
    queryKey: ['briefing', projectId, runId, 1],
    queryFn: () => api.getBriefing(projectId, runId, 1),
    retry: false,
  })

  useEffect(() => {
    if (!briefing.data?.artifact_path) {
      setBriefingBody('')
      return
    }
    fetch(projectFileUrl(projectId, briefing.data.artifact_path))
      .then((res) => (res.ok ? res.text() : ''))
      .then((txt) => setBriefingBody(txt))
      .catch(() => setBriefingBody(''))
  }, [briefing.data?.artifact_path, projectId])

  const queryClient = useQueryClient()

  const emit = useMutation({
    mutationFn: () => api.createBriefing(projectId, runId, 1),
    onSuccess: () => briefing.refetch(),
  })

  const approve = useMutation({
    mutationFn: () =>
      api.submitBriefingDecision(projectId, runId, 1, { action: 'approve' }),
    onSuccess: () => {
      briefing.refetch()
      setDecisionKind(null)
      onApproved?.()
    },
  })

  const [pendingModification, setPendingModification] = useState<string | null>(
    null,
  )
  const modify = useMutation({
    mutationFn: () =>
      api.submitBriefingDecision(projectId, runId, 1, {
        action: 'modify',
        modification: modification.trim() || null,
      }),
    onSuccess: () => {
      briefing.refetch()
      setPendingModification(modification.trim() || null)
      setDecisionKind(null)
    },
  })

  const runPhase = useMutation({
    mutationFn: async () => {
      await api.runPhase1(projectId, runId, {
        go_back_reason: pendingModification ?? undefined,
      })
      return waitForPhaseCompletion(projectId, runId, 1)
    },
    onSuccess: () => {
      toast({
        variant: 'success',
        title: `Phase ${1} complete`,
        description:
          'Charter, Intent Map, and Glossary were written — opening Gate G1.',
      })
      queryClient.invalidateQueries({ queryKey: ['gate', projectId, runId, 1] })
      queryClient.invalidateQueries({
        queryKey: ['artifact-index', projectId, runId],
      })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      setPendingModification(null)
      onPhaseComplete?.()
    },
  })

  const phase1Artifacts =
    (runPhase.data?.payload?.artifacts as Record<string, string> | undefined) ??
    null

  const isMissing =
    briefing.error instanceof ApiError && briefing.error.status === 404
  const isDecided =
    briefing.data?.status === 'approved' ||
    briefing.data?.status === 'modified' ||
    briefing.data?.status === 'go_back'

  const { whatText, whyText, costText } = useMemo(
    () => parseBriefingSections(briefingBody),
    [briefingBody],
  )

  const artifactUrl = briefing.data?.artifact_path
    ? projectFileUrl(projectId, briefing.data.artifact_path)
    : null

  return (
    <section
      data-testid={`briefing-${1}-card`}
      className="tf-scroll h-full overflow-y-auto bg-cb-bg text-cb-text"
      style={{ fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif" }}
    >
      <div className="mx-auto max-w-[760px] px-[34px] pb-20 pt-[26px]">
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-[7px] font-cb-mono text-[11px] tracking-[0.06em] text-cb-text-3"
        >
          <span>COMMAND CENTER</span>
          <span aria-hidden>›</span>
          <span>BRIEFING</span>
          <span aria-hidden>›</span>
          <span className="font-semibold text-cb-text-2">
            PHASE {1} BRIEFING
          </span>
        </nav>

        <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[linear-gradient(135deg,var(--cb-accent),var(--cb-accent-2))] px-[11px] py-[5px] text-[12px] font-bold text-white">
              <AlertCircle className="h-3 w-3" /> Needs you
            </span>
            <span className="rounded-full border border-cb-border bg-cb-panel-3 px-[11px] py-[5px] text-[12px] font-semibold text-cb-text-2">
              Phase {1} briefing
            </span>
            {briefing.data?.briefing_type === 'delta' ? (
              <span className="rounded-full border border-cb-accent-line bg-cb-accent-soft px-[11px] py-[5px] text-[12px] font-semibold text-cb-accent">
                Delta
              </span>
            ) : null}
            {briefing.data?.status ? (
              <span className="rounded-full border border-cb-border bg-cb-panel-3 px-[11px] py-[5px] text-[12px] font-semibold text-cb-text-2">
                {briefing.data.status}
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => emit.mutate()}
              disabled={emit.isPending}
              className="tf-btn inline-flex items-center gap-1.5 rounded-[9px] border border-cb-border bg-cb-panel px-[13px] py-[7px] text-[12.5px] font-semibold text-cb-text shadow-[0_1px_2px_rgba(20,16,12,.06)] disabled:opacity-60"
            >
              {emit.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              Re-emit
            </button>
            {artifactUrl ? (
              <a
                href={artifactUrl}
                target="_blank"
                rel="noreferrer"
                className="tf-btn inline-flex items-center gap-1.5 rounded-[9px] border border-cb-border bg-cb-panel px-[13px] py-[7px] text-[12.5px] font-semibold text-cb-text shadow-[0_1px_2px_rgba(20,16,12,.06)]"
              >
                <FileText className="h-3 w-3" /> Raw artifact
              </a>
            ) : null}
          </div>
        </div>

        <h2 className="mt-[18px] text-[30px] font-bold leading-[1.15] tracking-[-0.02em] text-cb-text">
          Phase {1} Briefing
        </h2>
        <p className="mt-[9px] text-[14.5px] leading-[1.55] text-cb-text-2">
          Review before Phase {1} spends any LLM tokens.
        </p>

        {briefing.isLoading ? (
          <div className="mt-6 flex items-center gap-2 text-[13px] text-cb-text-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading briefing…
          </div>
        ) : null}

        {isMissing ? (
          <div className="mt-6 flex items-start gap-3 rounded-[14px] border border-dashed border-cb-border bg-cb-panel-2 px-5 py-[18px]">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-cb-text-3" />
            <div>
              <div className="text-[13px] font-semibold text-cb-text">
                No briefing yet
              </div>
              <div className="mt-1 text-[12.5px] leading-[1.5] text-cb-text-2">
                Click <strong className="font-semibold">Re-emit</strong> to
                generate the Phase-{1} briefing — it's a deterministic
                summary, no LLM spend.
              </div>
            </div>
          </div>
        ) : null}

        {briefing.data ? (
          <>
            {whatText ? (
              <BriefCard label="WHAT I'M ABOUT TO DO" markdown={whatText} />
            ) : null}
            {whyText ? <BriefCard label="WHY" markdown={whyText} /> : null}
            {costText ? (
              <BriefCard label="COST & DURATION" markdown={costText} />
            ) : null}

            {briefing.data.artifact_path ? (
              <div className="mt-[22px] font-cb-mono text-[11.5px] text-cb-text-3">
                Artifact:{' '}
                <span className="text-cb-text-2">
                  {briefing.data.artifact_path}
                </span>
              </div>
            ) : null}

            {!isDecided ? (
              <>
                <div className="mt-[26px] font-cb-mono text-[10px] font-semibold tracking-[0.16em] text-cb-text-3">
                  YOUR DECISION
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => approve.mutate()}
                    disabled={approve.isPending}
                    title="Approve this briefing (no LLM spend) — the Run Phase button appears next."
                    className="tf-btn inline-flex items-center gap-[7px] rounded-[10px] border border-cb-success bg-cb-success px-[18px] py-[9px] text-[13.5px] font-bold text-white shadow-[0_1px_2px_rgba(20,16,12,.06)] disabled:opacity-70"
                  >
                    {approve.isPending ? (
                      <Loader2 className="h-[15px] w-[15px] animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-[15px] w-[15px]" />
                    )}
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => setDecisionKind('modify')}
                    title="Edit the briefing notes and re-run this phase with your modification."
                    className="tf-btn inline-flex items-center gap-[7px] rounded-[10px] border border-cb-border bg-cb-panel px-[18px] py-[9px] text-[13.5px] font-semibold text-cb-text shadow-[0_1px_2px_rgba(20,16,12,.06)]"
                  >
                    <Edit3 className="h-3.5 w-3.5" /> Edit
                  </button>
                </div>
              </>
            ) : null}

            {decisionKind === 'modify' ? (
              <div className="mt-5 space-y-2 rounded-[14px] border border-cb-border bg-cb-panel px-5 py-[18px]">
                <label
                  htmlFor={`cb-modify-${1}`}
                  className="font-cb-mono text-[10px] font-semibold tracking-[0.14em] text-cb-text-3"
                >
                  MODIFICATION NOTES
                </label>
                <textarea
                  id={`cb-modify-${1}`}
                  rows={3}
                  placeholder="Describe the change you'd like the orchestrator to apply…"
                  value={modification}
                  onChange={(e) => setModification(e.target.value)}
                  className="w-full rounded-[9px] border border-cb-border bg-cb-panel-2 px-3 py-2 text-[13.5px] text-cb-text outline-none focus:border-cb-accent-line focus:ring-1 focus:ring-cb-accent-line"
                />
                <div className="flex items-center gap-2.5 pt-1">
                  <button
                    type="button"
                    onClick={() => modify.mutate()}
                    disabled={modify.isPending}
                    className="tf-btn inline-flex items-center gap-1.5 rounded-[9px] bg-[linear-gradient(135deg,var(--cb-accent),var(--cb-accent-2))] px-[14px] py-2 text-[13px] font-semibold text-white disabled:opacity-70"
                  >
                    {modify.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <PencilLine className="h-4 w-4" />
                    )}
                    Submit edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setDecisionKind(null)}
                    className="tf-btn inline-flex items-center rounded-[9px] border border-cb-border bg-cb-panel px-[14px] py-2 text-[13px] font-semibold text-cb-text"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}

            {briefing.data.status === 'modified' && 1 === 1 ? (
              <div className="mt-5 space-y-2 rounded-[14px] border border-cb-border bg-cb-warn/10 px-5 py-[18px] text-[13px] text-cb-text">
                <div className="flex items-center gap-2 font-semibold text-cb-warn">
                  <PencilLine className="h-4 w-4" /> Modification recorded
                </div>
                <p className="text-[12.5px] leading-[1.5] text-cb-text-2">
                  Re-run Phase 1 to apply your modification to the
                  Contextualist's next pass.
                </p>
                {pendingModification ? (
                  <blockquote className="border-l-2 border-cb-accent-line pl-3 text-[12.5px] italic text-cb-text-2">
                    {pendingModification}
                  </blockquote>
                ) : null}
                <button
                  type="button"
                  onClick={() => runPhase.mutate()}
                  disabled={runPhase.isPending}
                  className="tf-btn mt-1 inline-flex items-center gap-1.5 rounded-[9px] bg-[linear-gradient(135deg,var(--cb-accent),var(--cb-accent-2))] px-[14px] py-2 text-[13px] font-semibold text-white disabled:opacity-70"
                >
                  {runPhase.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  Re-run Phase 1 with modification
                </button>
              </div>
            ) : null}

            {briefing.data.status === 'approved' && 1 === 1 ? (
              <div className="mt-4">
                <button
                  type="button"
                  data-testid={`phase-${1}-run-button`}
                  data-state={runPhase.isPending ? 'running' : 'ready'}
                  onClick={() => runPhase.mutate()}
                  disabled={runPhase.isPending}
                  className="tf-btn inline-flex items-center gap-2 rounded-[10px] bg-[linear-gradient(135deg,var(--cb-accent),var(--cb-accent-2))] px-[18px] py-[10px] text-[13.5px] font-bold text-white shadow-[0_3px_11px_-3px_var(--cb-accent)] disabled:opacity-70"
                >
                  {runPhase.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowRight className="h-4 w-4" />
                  )}
                  {runPhase.isPending
                    ? `Running Phase ${1}… (~60 s)`
                    : `Run Phase ${1}`}
                </button>
                {runPhase.isPending ? (
                  <p className="mt-2 text-[12px] text-cb-text-3">
                    The Contextualist is thinking — typically 40–60 seconds at
                    high effort. See the briefing's Cost &amp; Duration block
                    above for the active provider and model.
                  </p>
                ) : null}
              </div>
            ) : null}

            {phase1Artifacts ? (
              <div className="mt-3 rounded-[10px] border border-cb-success-border bg-cb-success-soft px-4 py-3 text-[12px] text-cb-success">
                <strong>Phase {1} complete.</strong> Artifacts written to{' '}
                <code className="font-cb-mono">{phase1Artifacts.charter}</code>,{' '}
                <code className="font-cb-mono">{phase1Artifacts.intent_map}</code>
                , and{' '}
                <code className="font-cb-mono">{phase1Artifacts.glossary}</code>.
              </div>
            ) : null}

            {runPhase.error ? (
              <div
                role="alert"
                className="mt-3 rounded-[10px] border border-cb-danger/40 bg-cb-danger/10 px-4 py-3 text-[12px] text-cb-danger"
              >
                <strong>Phase {1} failed.</strong>{' '}
                {(runPhase.error as ApiError).message}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  )
}
